import { TRANSFERZ_EXPERIENCE_ID } from 'constants/experience'
import qs from 'qs'
import { getAirportTransferSearchParams, orderAirportTransferItems } from 'lib/airportTransfer/airportTransferUtils'
import { getTransferCoordinates } from 'lib/airportTransfer/coordinates'
import { getTopPickCarsAirportTransferResultId } from 'lib/airportTransfer/airportTransferRecommendations'
import { getValidTransferOptions } from 'components/Experiences/ExperienceTransfers/ExperienceTransferUtils'
import {
  getFareFamilyBaggageInclusion,
  isCarryOnFeatureType,
} from 'lib/flights/flightTileBaggage'
import { parseReservationExpiresAt } from './experienceReservationUtils'

export const TRANSFER_QUOTE_COUNTDOWN_WINDOW_SECONDS = 15 * 60

export function getTransferQuoteExpiresAt(item: App.Checkout.AnyItem): string | undefined {
  if (item.itemType !== 'transfer' || item.experienceId !== TRANSFERZ_EXPERIENCE_ID) return undefined
  const expiresAt = item.metaData?.quoteExpiresAt
  return typeof expiresAt === 'string' && parseReservationExpiresAt(expiresAt) !== undefined ? expiresAt : undefined
}

export function isTransferQuoteExpired(item: App.Checkout.AnyItem, now = Date.now()): boolean {
  const expiresAt = getTransferQuoteExpiresAt(item)
  return expiresAt !== undefined && Date.parse(expiresAt) <= now
}

export function getExpiredTransferQuoteItems(items: Array<App.Checkout.AnyItem>, now = Date.now()): Array<App.Checkout.TransferItem> {
  return items.filter((item): item is App.Checkout.TransferItem => item.itemType === 'transfer' && isTransferQuoteExpired(item, now))
}

export function getEarliestActiveTransferQuoteExpiresAt(items: Array<App.Checkout.AnyItem>, now = Date.now()): string | undefined {
  return items.map(getTransferQuoteExpiresAt)
    .filter((expiry): expiry is string => !!expiry && Date.parse(expiry) > now)
    .sort((a, b) => Date.parse(a) - Date.parse(b))[0]
}

export function getTransferQuoteRecoverySearch(
  items: Array<App.Checkout.TransferItem>,
  source: 'cart' | 'checkout',
  cartId?: string,
  checkoutCartId?: string,
): string {
  const journeyId = items[0]?.metaData?.transferzJourneyId
  const journeyItems = typeof journeyId === 'string'
    ? items.filter(item => item.metaData?.transferzJourneyId === journeyId)
    : items.slice(0, 1)
  const transfers = orderAirportTransferItems(journeyItems.map(item => item.transfer))
  return qs.stringify({
    ...getAirportTransferSearchParams(transfers),
    pickUpLat: transfers[0]?.pickupPoint?.latitude,
    pickUpLng: transfers[0]?.pickupPoint?.longitude,
    dropOffLat: transfers[0]?.dropoffPoint?.latitude,
    dropOffLng: transfers[0]?.dropoffPoint?.longitude,
    transferQuoteRecovery: source,
    transferQuoteCartId: cartId,
    transferQuoteCheckoutCartId: checkoutCartId,
    transferQuoteJourneyId: typeof journeyId === 'string' ? journeyId : undefined,
  })
}

export function mapCarsAirportTransferTicketToOption(
  ticket: App.ExperienceItemTicket,
  direction: App.ExperienceTransferType,
): App.ExperienceTransferOption {
  return {
    id: ticket.productId,
    name: ticket.name,
    price: ticket.price,
    memberPrice: ticket.memberPrice,
    memberPriceCurrency: ticket.memberPriceCurrency,
    description: ticket.description ?? ticket.fareType,
    experienceId: ticket.experienceId,
    maxPassengers: ticket.bookingOptions?.maxPassengers ?? 0,
    maxCheckedBags: ticket.bookingOptions?.maxCheckedBags ?? 0,
    maxCarryOnBags: ticket.bookingOptions?.maxCarryOnBags ?? 0,
    hasChildSeats: ticket.bookingOptions?.childSeatTypes?.includes('INFANT-SEAT') ?? false,
    hasBoosterSeats: ticket.bookingOptions?.childSeatTypes?.includes('BOOSTER-SEAT') ?? false,
    maxTransferDistance: 100,
    transferTypes: [direction],
    extras: ticket.extras,
    taxesAndFees: ticket.taxesAndFees,
    purchaseLimit: ticket.purchaseLimit,
    bookByDate: ticket.bookByDate,
    rateStartDate: ticket.rateStartDate,
    rateEndDate: ticket.rateEndDate,
    discounts: ticket.discounts,
    freeCancellationUntil: ticket.freeCancellationUntil,
  }
}

export interface TransferzOneClickQuoteRequest {
  date: string
  currency: string
  ticketModesMap: Record<string, string>
}

function getTransferQuoteLocationParams(
  location: App.ExperienceTransferView['pickupPoint'],
  prefix: 'pickup' | 'dropoff',
): Record<string, string> | undefined {
  if (!location?.name) return undefined

  const id = location.iataCode || location.id
  if (id) {
    return {
      [`${prefix}Id`]: id,
      [`${prefix}Name`]: location.name,
    }
  }

  const coordinates = getTransferCoordinates(
    location.latitude,
    location.longitude,
  )
  if (!coordinates) return undefined

  return {
    [`${prefix}Name`]: location.name,
    [`${prefix}Lat`]: String(coordinates.lat),
    [`${prefix}Lng`]: String(coordinates.lng),
  }
}

export function buildTransferzOneClickQuoteRequest(
  transfer: App.ExperienceTransferView,
  currency: string,
  carryOnBags: number,
): TransferzOneClickQuoteRequest | undefined {
  const { date, time, travellers, baggage } = transfer
  const pickupParams = getTransferQuoteLocationParams(
    transfer.pickupPoint,
    'pickup',
  )
  const dropoffParams = getTransferQuoteLocationParams(
    transfer.dropoffPoint,
    'dropoff',
  )

  if (!date || !time || !pickupParams || !dropoffParams) {
    return undefined
  }

  return {
    date,
    currency,
    ticketModesMap: {
      type: transfer.type,
      pickupTime: time,
      ...pickupParams,
      ...dropoffParams,
      adults: String(travellers.adults),
      children: String(travellers.children ?? 0),
      infants: String(travellers.infants ?? 0),
      checkedLuggage: String(baggage.bags),
      carryOnLuggage: String(carryOnBags),
    },
  }
}

const TRANSFER_PASSENGER_TYPE_CODES = {
  adult: 'ADT',
  child: 'CHD',
  infant: 'INF',
} as const

export function getTransferzOneClickTravellers(
  passengers: Array<App.Checkout.FlightPassenger>,
): App.ExperienceTransferTravellers {
  return {
    adults: passengers.filter(passenger => passenger.type === 'adult').length,
    children: passengers.filter(passenger => passenger.type === 'child').length,
    infants: passengers.filter(passenger => passenger.type === 'infant').length,
  }
}

export function getTransferzOneClickBagCount(
  passengers: Array<App.Checkout.FlightPassenger>,
  hasAllowance: boolean,
  baggageInformation?: Array<App.InclusionBaggageInformation>,
): number {
  if (!hasAllowance) return 0

  return passengers.reduce((total, passenger) => {
    const passengerType = TRANSFER_PASSENGER_TYPE_CODES[passenger.type]
    const passengerAllowance = baggageInformation?.find(
      information => information.passengerType === passengerType,
    )
    const sharedAllowance = baggageInformation?.find(
      information => !information.passengerType,
    )
    const quantity =
      passengerAllowance?.quantity ?? sharedAllowance?.quantity

    if (
      quantity !== undefined &&
      Number.isInteger(quantity) &&
      quantity >= 0
    ) {
      return total + quantity
    }

    // Weight-only allowances count as one bag per adult/child.
    return total + (passenger.type === 'infant' ? 0 : 1)
  }, 0)
}

export function getTransferzOneClickJourneyBaggage(
  journey: App.Checkout.FlightItemViewJourney,
  passengers: Array<App.Checkout.FlightPassenger>,
) {
  const features = journey.fareFamily?.fareFamily.fareFeatures ?? []
  const inclusion = getFareFamilyBaggageInclusion(journey.fareFamily)
  const flights = journey.journeyFlight.flights

  const checkedInformation = features
    .filter(feature =>
      feature.type === 'checked_baggage' &&
      feature.status === 'INCLUDED',
    )
    .flatMap(feature => feature.extras?.baggageInformation ?? [])

  const carryOnInformation = features
    .filter(feature =>
      isCarryOnFeatureType(feature.type) &&
      feature.status === 'INCLUDED',
    )
    .flatMap(feature => feature.extras?.baggageInformation ?? [])

  const hasCheckedAllowance = inclusion.checked ??
    (flights.length > 0 &&
      flights.every(flight => flight.freeCheckedInBaggageIncluded))

  const hasCarryOnAllowance = inclusion.carryOn ??
    (flights.length > 0 &&
      flights.every(flight => !!flight.freeCarryOnBaggageDescription))

  return {
    checkedBags: getTransferzOneClickBagCount(
      passengers,
      hasCheckedAllowance,
      checkedInformation,
    ),
    carryOnBags: getTransferzOneClickBagCount(
      passengers,
      hasCarryOnAllowance,
      carryOnInformation,
    ),
  }
}

export function buildTransferzOneClickTransfer(
  defaultTransfer: App.ExperienceTransferView,
  pickup: { flight: App.Flight; date: string; time: string },
  travellers: App.ExperienceTransferTravellers,
  checkedBags: number,
): App.ExperienceTransferView {
  const isArrival = defaultTransfer.type === 'AIRPORT-TO-HOTEL'
  const { flight } = pickup
  const airportPoint = {
    name: isArrival
      ? flight.arrivalAirportName
      : flight.departingAirportName,
    iataCode: isArrival
      ? flight.arrivalAirport
      : flight.departingAirport,
  }
  const hotelPoint = {
    name: defaultTransfer.hotel.name,
    latitude: defaultTransfer.hotel.latitude,
    longitude: defaultTransfer.hotel.longitude,
  }

  return {
    ...defaultTransfer,
    date: pickup.date,
    time: pickup.time,
    flightNumber: `${flight.carrier}${flight.flightNumber}`,
    travellers,
    baggage: { ...defaultTransfer.baggage, bags: checkedBags },
    pickupPoint: isArrival ? airportPoint : hotelPoint,
    dropoffPoint: isArrival ? hotelPoint : airportPoint,
  }
}

export interface TransferzOneClickQuote {
  ticket: App.ExperienceItemTicket
  option: App.ExperienceTransferOption
  expiresAt?: string
}

export function getTransferzOneClickQuotes(
  slots: Array<App.ExperienceAvailabilityTimeSlot>,
  direction: App.ExperienceTransferType,
): Array<TransferzOneClickQuote> {
  const quotes = slots.flatMap(slot =>
    slot.tickets.map(ticket => ({
      ticket,
      expiresAt: slot.expiresAt,
      option: {
        ...mapCarsAirportTransferTicketToOption(ticket, direction),
        pickupPoint: slot.pickupPoint,
        dropoffPoint: slot.dropoffPoint,
      },
    })),
  )

  return Array.from(
    new Map(quotes.map(quote => [quote.ticket.id, quote] as const)).values(),
  )
}

export function getTransferzOneClickTopPick(
  quotes: Array<TransferzOneClickQuote>,
  transfer: App.ExperienceTransferView,
): TransferzOneClickQuote | undefined {
  const passengers =
    transfer.travellers.adults +
    (transfer.travellers.children ?? 0) +
    (transfer.travellers.infants ?? 0)

  const validOptions = getValidTransferOptions(
    {
      ...transfer,
      travellers: {
        ...transfer.travellers,
        adults: transfer.travellers.adults +
          (transfer.travellers.infants ?? 0),
      },
    },
    quotes.map(quote => quote.option),
  )
  const validIds = new Set(validOptions.map(option => option.id))
  const validQuotes = quotes.filter(quote => validIds.has(quote.option.id))

  const topPickId = getTopPickCarsAirportTransferResultId(
    validQuotes.map(quote => ({
      id: quote.ticket.id,
      maxPassengers: quote.option.maxPassengers,
      maxCheckedBags: quote.option.maxCheckedBags,
    })),
    passengers,
    transfer.baggage.bags,
  )

  return validQuotes.find(quote => quote.ticket.id === topPickId)
}
